import { useMemo } from "react";
import { apiFetch } from "../../../toolympus/api/core";
import { useItemActionWithConfirmation } from "../../../toolympus/api/useAction";
import { useLoadedData } from "../../../toolympus/hooks/useLoadedData"
import { createSelectSchema, FieldType, mergeSchema, Schema } from "../../../toolympus/hooks/useSchema";
import { useAttendeeProfileConfig } from "../Attendees/useAttendeeProfileConfiguration";
import { exportRecordsToXlsxSheet } from "../../../toolympus/api/xlsx";
import { useTextFilter } from "../../../toolympus/components/schemed/Filtering/useTextFilter";
import { useTableComments } from "../../../toolympus/components/Comments";
import { useUserNames } from "../../../toolympus/hooks/useUserNames";
import { CalendarEvent } from "./useEventsAdminList";
import { useBrowserStoredValue } from "../../../toolympus/hooks/useBrowserStoredValue";

export interface EventRegistration {
  _id: number;
  event_id?: number;
  email?: string;
  display_name?: string;
  registration_status?: string;
  registration_format?: string;
  info?: Record<string, any>;

  applied_discount_code?: string;
}

export const EventRegistrationSchema: Schema = {
  email: { label: "E-mail" },
  display_name: { label: "Full name" },
  registration_status: createSelectSchema([
    { value: "pending_confirmation", label: "Awaits confirmation" },
    { value: "pending_payment", label: "Awaits payment" },
    { value: "confirmed", label: "Confirmed" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" },
  ], { label: "Status" }),
  registration_format: createSelectSchema([
    { value: "online", label: "Online" },
    { value: "offline", label: "Offline" },
  ], { label: "Format" }),
  
  is_paid: { label: "Paid?", type: FieldType.bool },
  invoice_paid_at: { label: "Paid at", type: FieldType.datetime },
  invoice_nbr: { label: "Invoice #" },
  invoice_amount: { label: "Paid Amt.", type: FieldType.number },
  created_at: { label: "Created", type: FieldType.datetime },
  status_log: { label: "Status log", type: FieldType.textlong },

  applied_discount_code: { label: "D/code"},
  applied_discount_amount: { label: "D/amount", type: FieldType.number },
}

export const useEventRegistrations = (kind: "admin" | "coorg", event: CalendarEvent) => {
  const registrations = useLoadedData<EventRegistration[]>(`/api/registration/${kind}/event/${event._id}`, [], !!event && !!event._id && event._id > 0);
  const view = useBrowserStoredValue("all", undefined, "_riac_event_regs_view");

  const profileConfig = useAttendeeProfileConfig();

  const userNames = useUserNames();
  const comments = useTableComments("/api/comment", `eventreg_${event._id}`, {
    suppressLoad: !event || !event._id || event._id < 0 || kind === "coorg",
    getUserName: userNames.getUsername,
  });

  const approve = useItemActionWithConfirmation<EventRegistration, EventRegistration>(
    (registration) => {
      return apiFetch<EventRegistration>(`/api/registration/${kind}/registration/${registration._id}/approve`, "post")
        .then(reg => {
          registrations.setData(x => x.map(r => r._id === reg._id ? { ...r, ...reg } : r));
          return reg;
        })
    }, {
      skipConfirmation: true,
    }
  );

  const reject = useItemActionWithConfirmation<EventRegistration, EventRegistration>(
    (registration) => {
      return apiFetch<EventRegistration>(`/api/registration/${kind}/registration/${registration._id}/reject`, "post")
        .then(reg => {
          registrations.setData(x => x.map(r => r._id === reg._id ? { ...r, ...reg } : r));
          return reg;
        })
    }, {
      skipConfirmation: true,
    }
  );

  
  const filter = useTextFilter<EventRegistration>(r => `${r._id} ${r.email || ""} ${r.display_name || ""} ${r.applied_discount_code || ""}`);

  const registrationsWithProfiles = useMemo(() => {
    return registrations.data.map(r => ({
      ...(r.info || {}),
      ...r,
    }));
  }, [registrations]);

  const registrationsFiltered = useMemo(() => {
    const searchFiltered = filter.filterData(registrationsWithProfiles);
    if(view.value === "unprocessed") {
      return searchFiltered.filter(r => r.registration_status !== "paid" && r.registration_status !== "confirmed");
    } else if(view.value === "processed") {
      return searchFiltered.filter(r => r.registration_status === "paid" || r.registration_status === "confirmed");
    }
    return searchFiltered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationsWithProfiles, filter.filter])

  const schema = useMemo(() => mergeSchema(EventRegistrationSchema, profileConfig.schema), [profileConfig.schema]);

  const exportRecords = () => {
    exportRecordsToXlsxSheet({
      sheetTitle: "Registrations",
      schema,
      filename: `registrations.xlsx`,
      columns: [
        "registration_status",
        "email",
        "invoice_paid_at",
        ...(event.event_format === "hybrid"
            ? ["registration_format"]
            : []
        ),
        ...(kind === "coorg"
            ? []
            : ["invoice_paid_at",
              "invoice_amount",
              "invoice_nbr",
              "applied_discount_code",
              "applied_discount_amount"]),
        ...profileConfig.simpleFields.filter(f => !!f).map(f => (f as any)[0]),
        "created_at",
        ...(kind === "coorg"
          ? []
          : ["status_log",]),
      ],
      prepare: r => ({
        ...r,
        registration_status: schema["registration_status"].valueDict[r.registration_status || ""],
        registration_format: schema["registration_format"].valueDict[r.registration_format || ""],
      })
    }, registrationsFiltered);
  }


  return {
    ...registrations,
    data: registrationsFiltered,
    schema,
    profileFields: profileConfig.simpleFields,
    filter,
    view,


    approve,
    reject,

    exportRecords,

    comments,

    isActionRunning: approve.isRunning || reject.isRunning,
  }
}
